import type { AssistantSseEvent, AssistantSseType } from '@contracts/assistant';
import { envelopeError, offlineError } from '../../api/client';

/**
 * A turn is read with `fetch` and a stream reader (D84): the request carries a body, which `EventSource` cannot send.
 * The same reader serves confirm and reject, which answer SSE when they resume a conversation and JSON otherwise
 * (D71, D116), so the answer's content type decides how it is read.
 */

export type SseParser = { push(text: string): void; end(): void };
export type TurnAnswer = { kind: 'stream'; complete: boolean } | { kind: 'json'; data: unknown };

const SSE_TYPES: ReadonlySet<string> = new Set<AssistantSseType>(['turn_started', 'thinking', 'tool_started',
  'tool_done', 'confirm_required', 'message_delta', 'message_done', 'undo_available', 'error', 'turn_done']);

/** The WHATWG event-stream line rules, for the fields this API sends (event, data) and comments. */
export function createSseParser(onFrame: (event: string, data: string) => void): SseParser {
  let buffer = '';
  let event = 'message';
  let data: string[] = [];
  const line = (raw: string) => {
    if (raw === '') {
      if (data.length > 0) onFrame(event, data.join('\n'));
      event = 'message';
      data = [];
      return;
    }
    if (raw.startsWith(':')) return;                          // the heartbeat comment
    const colon = raw.indexOf(':');
    const field = colon === -1 ? raw : raw.slice(0, colon);
    let value = colon === -1 ? '' : raw.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  };
  return {
    push(text) {
      buffer += text;
      for (;;) {
        const at = buffer.search(/\r\n|\r|\n/);
        if (at === -1) return;
        // A CR that ends the chunk may be the first half of CRLF: wait for the next chunk.
        if (buffer[at] === '\r' && at === buffer.length - 1) return;
        const width = buffer.startsWith('\r\n', at) ? 2 : 1;
        line(buffer.slice(0, at));
        buffer = buffer.slice(at + width);
      }
    },
    // An event with no closing blank line is dropped (the event-stream rule).
    end() { buffer = ''; data = []; event = 'message'; },
  };
}

/** POSTs `body`; SSE frames go to `onEvent`. A JSON error answer throws `ApiClientError` (D69). */
export async function readTurn(path: string, body: unknown,
  o: { signal: AbortSignal; onEvent(e: AssistantSseEvent): void }): Promise<TurnAnswer> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST', credentials: 'same-origin', signal: o.signal,
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(body ?? {}),
    });
  } catch {
    if (o.signal.aborted) return { kind: 'stream', complete: false };
    throw offlineError();
  }
  if (!(response.headers.get('content-type') ?? '').startsWith('text/event-stream')) {
    let payload: unknown;
    try { payload = await response.json(); } catch { payload = undefined; }
    if (!response.ok) throw envelopeError(response.status, payload);
    return { kind: 'json', data: (payload as { data?: unknown } | undefined)?.data };
  }
  let complete = false;
  const parser = createSseParser((event, data) => {
    if (!SSE_TYPES.has(event) || o.signal.aborted) return;   // a newer event kind is ignored, not an error
    let parsed: unknown;
    try { parsed = JSON.parse(data); } catch { return; }
    if (event === 'turn_done') complete = true;
    o.onEvent({ type: event, data: parsed } as AssistantSseEvent);
  });
  if (response.body === null) return { kind: 'stream', complete };
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      parser.push(value);
    }
  } catch {
    // Aborted, or the connection dropped: the server turn goes on (D70); the caller reloads the conversation.
  }
  parser.end();
  return { kind: 'stream', complete };
}
